import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { extractErr } from "../lib/api";
import { Search, Send, Sparkles, RefreshCw, Plus, X, Loader2, Phone, Mail, Tag, ArrowLeft, Archive, ArchiveRestore, Paperclip, Smile, Zap, FileText } from "lucide-react";
import { toast } from "sonner";

const STAGES = ["New", "Contact", "Consider", "Register", "Lost"];

const STAGE_LABELS = {
  New: "全新",
  Contact: "沟通中",
  Consider: "考虑报名",
  Register: "成功报名",
  Lost: "没回复",
};

const stageBadge = (s) => ({
  New: "bg-slate-100 text-slate-700 border-slate-300",
  Contact: "bg-blue-50 text-blue-800 border-blue-200",
  Consider: "bg-amber-50 text-amber-800 border-amber-200",
  Register: "bg-emerald-50 text-emerald-800 border-emerald-200",
  Lost: "bg-red-50 text-red-800 border-red-200",
}[s] || "bg-slate-100 text-slate-700");

function initials(name) {
  return (name || "?").split(" ").slice(0, 2).map(s => s[0]?.toUpperCase()).join("");
}

function relTime(iso) {
  if (!iso) return "";
  const d = new Date(iso), now = new Date(), diff = (now - d) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff/60)}m`;
  if (diff < 86400) return `${Math.floor(diff/3600)}h`;
  return d.toLocaleDateString();
}

export default function Inbox() {
  const [params, setParams] = useSearchParams();
  const [convs, setConvs] = useState([]);
  const [selectedId, setSelectedId] = useState(params.get("c"));
  const [messages, setMessages] = useState([]);
  const [lead, setLead] = useState(null);
  const [conv, setConv] = useState(null);
  const [text, setText] = useState("");
  const [search, setSearch] = useState("");
  const [aiDrafting, setAiDrafting] = useState(false);
  const [aiDraft, setAiDraft] = useState("");
  const [showSimulate, setShowSimulate] = useState(false);
  const [leadsData, setLeadsData] = useState([]);
  const [activeInboxFilter, setActiveInboxFilter] = useState("all"); // "all" | "unread" | tag string
  const [inboxTab, setInboxTab] = useState("active"); // "active" | "archived"
  const [lightbox, setLightbox] = useState(null); // media_url when opening lightbox
  const [showTplPicker, setShowTplPicker] = useState(false);
  const [watiTpls, setWatiTpls] = useState([]);
  const [showEmoji, setShowEmoji] = useState(false);
  const [quickReplies, setQuickReplies] = useState([]);
  const [showQR, setShowQR] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const fileRef = useRef(null);
  const scrollRef = useRef(null);

  const loadConvs = () => api.get(`/conversations?archived=${inboxTab === "archived"}`).then(r => setConvs(r.data));
  const loadLeads = () => api.get("/leads").then(r => setLeadsData(r.data));

  useEffect(() => { loadConvs(); loadLeads(); const t = setInterval(() => { loadConvs(); loadLeads(); }, 8000); return () => clearInterval(t); }, [inboxTab]);

  useEffect(() => {
    api.get("/quick-replies").then(r => setQuickReplies(r.data || [])).catch(() => {});
    api.get("/wati/templates").then(r => setWatiTpls((r.data.templates || []).filter(t => (t.status || "").toUpperCase() === "APPROVED" || t.source === "local"))).catch(() => {});
  }, []);

  const attachFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!selectedId) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("最大 5MB"); return; }
    setAttaching(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data: up } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Send as message with media_path (backend send_message extension)
      const { data: msg } = await api.post(`/conversations/${selectedId}/messages`, { text: text.trim() || "", media_path: up.path });
      setMessages(m => [...m, msg]);
      setText("");
      toast.success("已发送");
      loadConvs();
    } catch (e) { toast.error(extractErr(e)); }
    finally { setAttaching(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const insertEmoji = (em) => { setText(t => t + em); setShowEmoji(false); };
  const applyQR = (qr) => { setText(qr.text); setShowQR(false); };

  const archiveConv = async (convId, e) => {
    e?.stopPropagation();
    try {
      await api.patch(`/conversations/${convId}/archive`);
      toast.success("已归档");
      if (selectedId === convId) setSelectedId(null);
      loadConvs();
    } catch (e) { toast.error(extractErr(e)); }
  };

  const unarchiveConv = async (convId, e) => {
    e?.stopPropagation();
    try {
      await api.patch(`/conversations/${convId}/unarchive`);
      toast.success("已恢复");
      loadConvs();
    } catch (e) { toast.error(extractErr(e)); }
  };

  useEffect(() => {
    if (!selectedId) { setMessages([]); setLead(null); setConv(null); return; }
    setParams((prev) => { const p = new URLSearchParams(prev); p.set("c", selectedId); return p; });
    api.get(`/conversations/${selectedId}`).then(r => { setLead(r.data.lead); setConv(r.data.conversation); });
    api.get(`/conversations/${selectedId}/messages`).then(r => {
      setMessages(r.data);
      setTimeout(() => scrollRef.current?.scrollTo({ top: 99999, behavior: "smooth" }), 50);
    });
    setAiDraft("");
  }, [selectedId]);

  // Build phone → tags map for client-side filtering
  const tagsByPhone = useMemo(() => {
    const m = {};
    (leadsData || []).forEach(l => { if (l.phone) m[l.phone] = l.tags || []; });
    return m;
  }, [leadsData]);

  const allInboxTags = useMemo(() => {
    const s = new Set();
    (leadsData || []).forEach(l => (l.tags || []).forEach(t => s.add(t)));
    return Array.from(s).sort();
  }, [leadsData]);

  const filteredConvs = useMemo(() => {
    let list = convs;
    // Apply tag / unread filter (client-side, no API calls)
    if (activeInboxFilter === "unread") {
      list = list.filter(c => (c.unread_count || 0) > 0);
    } else if (activeInboxFilter !== "all") {
      list = list.filter(c => (tagsByPhone[c.phone] || []).includes(activeInboxFilter));
    }
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter(c => (c.lead_name || "").toLowerCase().includes(q) || (c.phone || "").includes(q) || (c.last_message || "").toLowerCase().includes(q));
  }, [convs, search, activeInboxFilter, tagsByPhone]);

  const sendMessage = async (msgText) => {
    const t = (msgText ?? text).trim();
    if (!t || !selectedId) return;
    try {
      const { data } = await api.post(`/conversations/${selectedId}/messages`, { text: t });
      setMessages(m => [...m, data]);
      setText(""); setAiDraft("");
      setTimeout(() => scrollRef.current?.scrollTo({ top: 99999, behavior: "smooth" }), 50);
      loadConvs();
    } catch (e) { toast.error(extractErr(e)); }
  };

  const generateDraft = async () => {
    if (!selectedId) return;
    setAiDrafting(true); setAiDraft("");
    try {
      const { data } = await api.post(`/conversations/${selectedId}/ai-draft`, {});
      // typewriter effect
      const full = data.draft || "";
      let i = 0;
      const iv = setInterval(() => {
        i += 2;
        setAiDraft(full.slice(0, i));
        if (i >= full.length) clearInterval(iv);
      }, 12);
    } catch (e) { toast.error(extractErr(e)); }
    finally { setAiDrafting(false); }
  };

  const updateStage = async (newStage) => {
    if (!lead) return;
    try {
      const { data } = await api.patch(`/leads/${lead.id}`, { stage: newStage });
      setLead(data);
      toast.success(`Stage → ${newStage}`);
    } catch (e) { toast.error(extractErr(e)); }
  };

  return (
    <div className="flex h-[calc(100vh-56px)] md:h-screen bg-white">
      {/* Conversation list · Desktop: always visible · Mobile: hide when chat selected */}
      <div className={`${selectedId ? "hidden md:flex" : "flex"} w-full md:w-80 border-r border-slate-200 flex-col bg-white flex-shrink-0`}>
        <div className="p-4 border-b border-slate-200">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-700 font-semibold">Omnichannel</div>
              <div className="font-display text-2xl font-black tracking-tighter">Inbox</div>
            </div>
            <button
              data-testid="new-conversation-btn"
              onClick={() => setShowSimulate(true)}
              className="p-2 border border-slate-300 hover:border-emerald-700 hover:text-emerald-700 rounded-sm transition-colors"
              title="发起新对话 · Start new conversation"
            >
              <Plus size={16} />
            </button>
          </div>

          {/* Active / Archived tab switch */}
          <div className="flex gap-1 mb-3 border border-slate-200 rounded-sm p-0.5 bg-slate-50">
            <button
              data-testid="inbox-tab-active"
              onClick={() => { setInboxTab("active"); setSelectedId(null); }}
              className={`flex-1 py-1 text-[11px] font-semibold rounded-sm ${inboxTab === "active" ? "bg-white shadow-sm text-emerald-700" : "text-slate-500 hover:text-slate-800"}`}
            >Active <span className="text-slate-400">进行中</span></button>
            <button
              data-testid="inbox-tab-archived"
              onClick={() => { setInboxTab("archived"); setSelectedId(null); }}
              className={`flex-1 py-1 text-[11px] font-semibold rounded-sm ${inboxTab === "archived" ? "bg-white shadow-sm text-slate-700" : "text-slate-500 hover:text-slate-800"}`}
            >Archived <span className="text-slate-400">归档</span></button>
          </div>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="inbox-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, phone, message..."
              className="w-full pl-9 pr-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-sm focus:outline-none focus:border-emerald-700"
            />
          </div>
          {/* Dynamic Tag Filter Row - purely client-side */}
          <div className="flex items-center gap-1.5 mt-2.5 overflow-x-auto scrollbar-thin pb-1">
            <InboxChip
              label="全部 All"
              testid="inbox-filter-all"
              active={activeInboxFilter === "all"}
              onClick={() => setActiveInboxFilter("all")}
              count={convs.length}
            />
            <InboxChip
              label="未回复 🔴"
              testid="inbox-filter-unread"
              active={activeInboxFilter === "unread"}
              onClick={() => setActiveInboxFilter("unread")}
              count={convs.filter(c => (c.unread_count || 0) > 0).length}
              accent="red"
            />
            {allInboxTags.map(t => {
              const count = convs.filter(c => (tagsByPhone[c.phone] || []).includes(t)).length;
              if (count === 0) return null;
              return (
                <InboxChip
                  key={t}
                  label={t}
                  testid={`inbox-filter-${t}`}
                  active={activeInboxFilter === t}
                  onClick={() => setActiveInboxFilter(t)}
                  count={count}
                />
              );
            })}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto scrollbar-thin">
          {filteredConvs.length === 0 && (
            <div className="p-6 text-center text-sm text-slate-500">
              {inboxTab === "archived"
                ? "No archived conversations · 无归档对话"
                : <>暂无对话 · 点击右上角 <Plus size={12} className="inline"/> 发起新的 WhatsApp 对话</>}
            </div>
          )}
              {filteredConvs.map(c => {
                const channelBadge = c.channel === "messenger"
                  ? { label: "Messenger", cls: "bg-blue-100 text-blue-800 border-blue-300" }
                  : c.channel === "instagram"
                  ? { label: "Instagram", cls: "bg-pink-100 text-pink-800 border-pink-300" }
                  : { label: "WhatsApp", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" };
                return (
            <button
              key={c.id}
              data-testid={`conv-item-${c.id}`}
              onClick={() => setSelectedId(c.id)}
              className={`group relative w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors ${selectedId === c.id ? "bg-emerald-50/60 border-l-2 border-l-emerald-700" : ""}`}
            >
              {inboxTab === "active" ? (
                <button
                  data-testid={`archive-btn-${c.id}`}
                  onClick={(e) => archiveConv(c.id, e)}
                  title="Archive · 归档"
                  className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-opacity p-1 border border-slate-300 bg-white hover:border-emerald-700 hover:text-emerald-700 rounded-sm z-10"
                >
                  <Archive size={11} />
                </button>
              ) : (
                <button
                  data-testid={`unarchive-btn-${c.id}`}
                  onClick={(e) => unarchiveConv(c.id, e)}
                  title="Restore · 取消归档"
                  className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-opacity p-1 border border-slate-300 bg-white hover:border-emerald-700 hover:text-emerald-700 rounded-sm z-10"
                >
                  <ArchiveRestore size={11} />
                </button>
              )}
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-emerald-700 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                  {initials(c.lead_name)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold text-sm truncate">{c.lead_name}</div>
                    <div className="text-[10px] text-slate-400 flex-shrink-0">{relTime(c.last_message_at)}</div>
                  </div>
                  <div className="text-xs text-slate-500 truncate mt-0.5">{c.last_message || "No messages yet"}</div>
                  <div className="flex items-center justify-between mt-1 gap-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span data-testid={`conv-channel-${c.id}`} className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 border rounded-sm ${channelBadge.cls}`}>{channelBadge.label}</span>
                      {c.ad_source && (
                        <span title={c.ad_source} className="text-[9px] font-semibold text-purple-800 bg-purple-50 border border-purple-200 rounded-sm px-1.5 py-0.5 truncate max-w-[110px]">📢 {c.ad_source}</span>
                      )}
                    </div>
                    {c.unread_count > 0 && (
                      <span className="text-[9px] font-bold px-1.5 py-0.5 bg-orange-500 text-white rounded-sm flex-shrink-0">{c.unread_count}</span>
                    )}
                  </div>
                </div>
              </div>
            </button>
                );
              })}
        </div>
      </div>

      {/* Chat pane */}
      <div className={`${selectedId ? "flex" : "hidden md:flex"} flex-1 flex-col min-w-0 bg-slate-50`}>
        {!selectedId ? (
          <div className="flex-1 flex items-center justify-center flex-col">
            <div className="w-16 h-16 bg-white border border-slate-200 rounded-sm flex items-center justify-center mb-4">
              <Sparkles size={26} className="text-orange-500" />
            </div>
            <div className="font-display text-xl font-bold mb-1">Pick a conversation</div>
            <div className="text-sm text-slate-500 max-w-sm text-center">来自 WhatsApp · Messenger · Instagram 的消息统一显示在左侧。选择一条对话开始回复,或让 AI 起草。</div>
          </div>
        ) : (
          <>
            {/* Chat header */}
            <div className="px-4 md:px-6 py-3 md:py-4 bg-white border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1">
                <button
                  data-testid="mobile-back-btn"
                  onClick={() => setSelectedId(null)}
                  className="md:hidden p-1.5 -ml-1 border border-slate-200 rounded-sm hover:bg-slate-50 flex-shrink-0"
                  aria-label="Back to inbox list"
                >
                  <ArrowLeft size={16} />
                </button>
                <div className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-emerald-700 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                  {initials(lead?.name)}
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-sm md:text-base truncate">{lead?.name}</div>
                  <div className="text-xs text-slate-500 font-mono truncate">{conv?.phone}</div>
                </div>
              </div>
              <div className="flex items-center gap-1.5 md:gap-2 flex-shrink-0">
                {conv?.channel && (
                  <span data-testid="chat-header-channel" className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 border rounded-sm ${
                    conv.channel === "messenger" ? "bg-blue-100 text-blue-800 border-blue-300"
                    : conv.channel === "instagram" ? "bg-pink-100 text-pink-800 border-pink-300"
                    : "bg-emerald-100 text-emerald-800 border-emerald-300"
                  }`}>{conv.channel}</span>
                )}
                {conv?.ad_source && (
                  <span title={conv.ad_source} className="text-[10px] font-semibold text-purple-800 bg-purple-50 border border-purple-200 rounded-sm px-2 py-1 max-w-[200px] truncate">📢 {conv.ad_source}</span>
                )}
                <span className={`text-[11px] font-bold tracking-wider px-2 py-1 border ${stageBadge(lead?.stage)}`}>{STAGE_LABELS[lead?.stage] || lead?.stage}</span>
              </div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto scrollbar-thin px-3 md:px-6 py-4 md:py-6 space-y-3">
              {messages.map(m => (
                <div key={m.id} className={`flex ${m.direction === "outbound" ? "justify-end" : "justify-start"} animate-fade-in`}>
                  <div className={`max-w-[75%] px-4 py-2.5 text-sm leading-relaxed ${
                    m.direction === "outbound"
                      ? "bg-emerald-700 text-white rounded-sm border border-emerald-800"
                      : "bg-white text-slate-900 rounded-sm border border-slate-200"
                  }`}>
                    {m.media_url && (
                      <img
                        data-testid={`msg-image-${m.id}`}
                        src={m.media_url}
                        alt={m.media_type || "attachment"}
                        onClick={() => setLightbox(m.media_url)}
                        className="max-w-[240px] max-h-[240px] object-cover rounded-sm border border-white/30 cursor-zoom-in mb-1 hover:opacity-90"
                      />
                    )}
                    {m.text && !(m.text.startsWith("[") && m.text.endsWith("]") && m.media_url) && (
                      <div className="whitespace-pre-wrap break-words">{m.text}</div>
                    )}
                    {m.kind === "wati_template" && (
                      <div className={`text-[9px] mt-1 uppercase tracking-wider font-bold ${m.direction === "outbound" ? "text-emerald-200" : "text-purple-600"}`}>
                        📋 Template · {m.template_name}
                      </div>
                    )}
                    <div className={`text-[10px] mt-1 ${m.direction === "outbound" ? "text-emerald-100" : "text-slate-400"}`}>
                      {relTime(m.timestamp)} {m.direction === "outbound" && `· ${m.status || "sent"}`}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* AI Draft area */}
            {(aiDrafting || aiDraft) && (
              <div className="mx-6 mb-3 p-4 bg-orange-50 border border-orange-300 rounded-sm shadow-brutal-accent animate-fade-in">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Sparkles size={13} className="text-orange-600" />
                    <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-orange-700">AI Draft · Claude Sonnet 4.5</span>
                  </div>
                  <button data-testid="dismiss-ai-draft" onClick={() => setAiDraft("")} className="text-orange-700 hover:text-orange-900"><X size={14} /></button>
                </div>
                <div data-testid="ai-draft-text" className={`text-sm text-slate-900 leading-relaxed min-h-[20px] ${aiDrafting && !aiDraft ? "typewriter-cursor" : ""}`}>
                  {aiDraft || (aiDrafting && <span className="text-slate-400 italic">Thinking…</span>)}
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <button
                    data-testid="use-ai-draft"
                    disabled={!aiDraft || aiDrafting}
                    onClick={() => { setText(aiDraft); setAiDraft(""); }}
                    className="px-3 py-1.5 bg-slate-950 text-white text-xs font-semibold rounded-sm hover:bg-emerald-700 disabled:opacity-40"
                  >Use draft</button>
                  <button
                    data-testid="send-ai-draft"
                    disabled={!aiDraft || aiDrafting}
                    onClick={() => sendMessage(aiDraft)}
                    className="px-3 py-1.5 border border-emerald-700 text-emerald-700 text-xs font-semibold rounded-sm hover:bg-emerald-50 disabled:opacity-40"
                  >Send as-is</button>
                  <button
                    data-testid="regenerate-ai-draft"
                    disabled={aiDrafting}
                    onClick={generateDraft}
                    className="ml-auto p-1.5 text-orange-700 hover:bg-orange-100 rounded-sm disabled:opacity-40"
                    title="Regenerate"
                  >
                    <RefreshCw size={13} className={aiDrafting ? "animate-spin" : ""} />
                  </button>
                </div>
              </div>
            )}

            {/* Composer */}
            <div className="px-6 py-3 bg-white border-t border-slate-200 relative">
              {showEmoji && (
                <div className="absolute bottom-full left-6 mb-2 p-2 bg-white border border-slate-300 rounded-sm shadow-lg z-30 grid grid-cols-8 gap-1 max-w-[280px]" data-testid="emoji-picker">
                  {["😊","😂","🙏","👍","❤️","🎉","😍","🥰","😢","😭","😅","🙌","👋","💪","🔥","✨","🌟","💯","🤔","😎","🙈","🥺","😌","😉","👏","💖","🎊","🌸","☀️","🌈","💐","🌺","😊","🎂","🎁","💝"].map((em, i) => (
                    <button key={i} type="button" onClick={() => insertEmoji(em)} className="text-lg hover:bg-slate-100 rounded-sm w-8 h-8">{em}</button>
                  ))}
                </div>
              )}
              {showQR && (
                <div className="absolute bottom-full left-6 mb-2 bg-white border border-slate-300 rounded-sm shadow-lg z-30 min-w-[280px] max-h-64 overflow-y-auto" data-testid="quick-replies-list">
                  <div className="p-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500 border-b border-slate-200">Quick Replies · 快速回复</div>
                  {quickReplies.length === 0 && <div className="p-3 text-xs text-slate-400 text-center">还没设置快速回复<br/><span className="text-[10px]">Team → 快速回复（待建）</span></div>}
                  {quickReplies.map(qr => (
                    <button key={qr.id} type="button" onClick={() => applyQR(qr)} className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-b-0">
                      <div className="text-[10px] font-mono text-emerald-700">/{qr.shortcut}</div>
                      <div className="text-xs text-slate-800 truncate">{qr.text}</div>
                    </button>
                  ))}
                </div>
              )}
              <div className="flex items-end gap-2">
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={attachFile} data-testid="composer-file-input" />
                <button data-testid="composer-attach" type="button" onClick={() => fileRef.current?.click()} disabled={attaching} title="Attach image · 附件" className="p-2 border border-slate-300 hover:border-emerald-700 hover:text-emerald-700 rounded-sm disabled:opacity-40">
                  {attaching ? <Loader2 size={15} className="animate-spin"/> : <Paperclip size={15} />}
                </button>
                <button data-testid="composer-emoji" type="button" onClick={() => { setShowEmoji(!showEmoji); setShowQR(false); }} title="Emoji" className="p-2 border border-slate-300 hover:border-amber-500 hover:text-amber-500 rounded-sm">
                  <Smile size={15} />
                </button>
                <button data-testid="composer-qr" type="button" onClick={() => { setShowQR(!showQR); setShowEmoji(false); }} title="Quick reply · 快速回复 (/) " className="p-2 border border-slate-300 hover:border-blue-600 hover:text-blue-600 rounded-sm">
                  <Zap size={15} />
                </button>
                <button data-testid="composer-tpl" type="button" onClick={() => setShowTplPicker(true)} title="发送 WhatsApp 模板" className="p-2 border border-purple-300 text-purple-700 hover:border-purple-700 hover:bg-purple-50 rounded-sm">
                  <FileText size={15} />
                </button>
                <textarea
                  data-testid="composer-textarea"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Write a reply... (输入 / 打开快速回复)"
                  rows={2}
                  className="flex-1 px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-sm focus:outline-none focus:border-emerald-700 resize-none"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) sendMessage();
                    else if (e.key === "/" && text === "") { e.preventDefault(); setShowQR(true); }
                  }}
                />
                <button
                  data-testid="ai-draft-btn"
                  onClick={generateDraft}
                  disabled={aiDrafting}
                  className="px-3 py-2 bg-white border-2 border-orange-500 text-orange-600 text-sm font-semibold rounded-sm hover:bg-orange-50 shadow-brutal-accent disabled:opacity-50 flex items-center gap-1.5"
                >
                  {aiDrafting ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                  AI Draft
                </button>
                <button
                  data-testid="send-btn"
                  onClick={() => sendMessage()}
                  disabled={!text.trim()}
                  className="px-4 py-2 bg-emerald-700 text-white text-sm font-semibold rounded-sm hover:bg-emerald-800 disabled:opacity-40 flex items-center gap-1.5"
                >
                  <Send size={14} /> Send
                </button>
              </div>
              <div className="text-[10px] text-slate-400 mt-1.5">Cmd/Ctrl + Enter to send · Type / for quick replies</div>
            </div>
          </>
        )}
      </div>

      {/* Right sidebar - Lead context */}
      {selectedId && lead && (
        <div className="hidden lg:block w-80 border-l border-slate-200 bg-white flex-shrink-0 overflow-y-auto scrollbar-thin">
          <div className="p-5 border-b border-slate-200">
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-semibold">Lead profile</div>
            <div className="font-display text-xl font-bold mt-1 tracking-tight">{lead.name}</div>
          </div>
          <AiSummarySection lead={lead} onUpdated={setLead} />
          <div className="p-5 space-y-4 text-sm">
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold flex items-center gap-1.5"><Phone size={11}/> Phone</label>
              <div className="font-mono text-slate-800 mt-1">{lead.phone}</div>
            </div>
            <ProfileField
              label="Title / Role (职位)"
              value={lead.title || ""}
              placeholder="院长 / 校长 / 老师"
              testid="lead-title"
              onSave={async (v) => { const { data } = await api.patch(`/leads/${lead.id}`, { title: v }); setLead(data); }}
            />
            <ProfileField
              label="Organization (机构)"
              value={lead.organization || ""}
              placeholder="育才安亲中心"
              testid="lead-organization"
              onSave={async (v) => { const { data } = await api.patch(`/leads/${lead.id}`, { organization: v }); setLead(data); }}
            />
            <ProfileField
              label="Last Inquired Course (上次咨询)"
              value={lead.last_inquired_course || ""}
              placeholder="SEL初阶认证"
              testid="lead-last-course"
              onSave={async (v) => { const { data } = await api.patch(`/leads/${lead.id}`, { last_inquired_course: v }); setLead(data); }}
            />
            <SelectFieldWithMeta
              label="State (州属)"
              metaKey="states"
              value={lead.state || ""}
              testid="lead-state"
              onSave={async (v) => { const { data } = await api.patch(`/leads/${lead.id}`, { state: v }); setLead(data); }}
            />
            <SelectFieldWithMeta
              label="Job Role (岗位)"
              metaKey="job_roles"
              value={lead.job_role || ""}
              testid="lead-job-role"
              onSave={async (v) => { const { data } = await api.patch(`/leads/${lead.id}`, { job_role: v }); setLead(data); }}
            />
            <SelectFieldWithMeta
              label="Client Category (B2C/B2B)"
              metaKey="client_categories"
              value={lead.client_category || ""}
              testid="lead-client-category"
              onSave={async (v) => { const { data } = await api.patch(`/leads/${lead.id}`, { client_category: v }); setLead(data); }}
            />
            {lead.email && (
              <div>
                <label className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold flex items-center gap-1.5"><Mail size={11}/> Email</label>
                <div className="text-slate-800 mt-1 break-all">{lead.email}</div>
              </div>
            )}
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold flex items-center gap-1.5 mb-1.5"><Tag size={11}/> Tags</label>
              <TagChipsEditor lead={lead} onUpdated={setLead} />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-1.5 block">Stage</label>
              <div className="grid grid-cols-2 gap-1.5">
                {STAGES.map(s => (
                  <button
                    key={s}
                    data-testid={`stage-btn-${s}`}
                    onClick={() => updateStage(s)}
                    className={`text-[11px] font-semibold tracking-wider py-1.5 px-2 border rounded-sm transition-colors ${
                      lead.stage === s ? stageBadge(s) + " ring-1 ring-slate-400" : "bg-white border-slate-200 hover:bg-slate-50"
                    }`}
                  >{STAGE_LABELS[s] || s}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-1 block">Notes</label>
              <NotesEditor lead={lead} onSaved={(u) => setLead(u)} />
            </div>
          </div>
        </div>
      )}

      {showSimulate && <NewConversationModal onClose={() => setShowSimulate(false)} onDone={(convId) => { setShowSimulate(false); loadConvs(); if (convId) setSelectedId(convId); }} />}
      {showTplPicker && selectedId && (
        <InboxTemplateModal
          conversationId={selectedId}
          leadName={lead?.name}
          templates={watiTpls}
          onClose={() => setShowTplPicker(false)}
          onSent={(msg) => { setShowTplPicker(false); setMessages(m => [...m, msg]); loadConvs(); toast.success("Template 已发送"); }}
        />
      )}
      {lightbox && (
        <div className="fixed inset-0 bg-black/85 flex items-center justify-center z-[60] p-4" onClick={() => setLightbox(null)} data-testid="lightbox">
          <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white"><X size={20} /></button>
          <img src={lightbox} alt="attachment" className="max-w-full max-h-full object-contain" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}

function AiSummarySection({ lead, onUpdated }) {
  const [busy, setBusy] = useState(false);
  const gen = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/leads/${lead.id}/ai-summary`);
      onUpdated(data);
      toast.success("AI summary refreshed");
    } catch (e) { toast.error(extractErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="border-b border-slate-200 bg-orange-50/40 p-5">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.2em] text-orange-700 font-bold flex items-center gap-1.5">
          <Sparkles size={11} /> AI Summary
        </div>
        <button
          data-testid="regen-ai-summary"
          onClick={gen}
          disabled={busy}
          className="text-[10px] font-semibold text-orange-700 hover:underline disabled:opacity-40"
        >
          {busy ? "Analyzing…" : (lead.ai_summary ? "Refresh" : "Generate")}
        </button>
      </div>
      {lead.ai_summary ? (
        <div data-testid="ai-summary-text" className="text-xs text-slate-700 leading-relaxed whitespace-pre-line">{lead.ai_summary}</div>
      ) : (
        <div className="text-xs text-slate-500 italic">Click Generate to let AI summarise this lead from the conversation.</div>
      )}
    </div>
  );
}

function ProfileField({ label, value, placeholder, testid, onSave }) {
  const [v, setV] = useState(value || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setV(value || ""); }, [value]);
  const save = async () => {
    if (v === (value || "")) return;
    setBusy(true);
    try { await onSave(v); } catch (e) { toast.error(extractErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-1 block">{label}</label>
      <input
        data-testid={testid}
        value={v}
        onChange={(e) => setV(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
        placeholder={placeholder}
        disabled={busy}
        className="w-full px-2 py-1.5 text-sm bg-slate-50 border border-slate-200 rounded-sm focus:outline-none focus:border-emerald-700 focus:bg-white disabled:opacity-60"
      />
    </div>
  );
}

function SelectFieldWithMeta({ label, metaKey, value, testid, onSave }) {
  const [busy, setBusy] = useState(false);
  const [options, setOptions] = useState([]);
  useEffect(() => {
    api.get("/lead-meta").then(r => setOptions(r.data[metaKey] || []));
  }, [metaKey]);
  const change = async (e) => {
    const v = e.target.value;
    setBusy(true);
    try { await onSave(v); } catch (err) { toast.error(extractErr(err)); }
    finally { setBusy(false); }
  };
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-1 block">{label}</label>
      <select
        data-testid={testid}
        value={value}
        onChange={change}
        disabled={busy}
        className="w-full px-2 py-1.5 text-sm bg-slate-50 border border-slate-200 rounded-sm focus:outline-none focus:border-emerald-700 focus:bg-white disabled:opacity-60"
      >
        <option value="">-- 未设定 --</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function TagChipsEditor({ lead, onUpdated }) {
  const [adding, setAdding] = useState(false);
  const [val, setVal] = useState("");
  const tags = lead.tags || [];
  const patch = async (newTags) => {
    try {
      const { data } = await api.patch(`/leads/${lead.id}`, { tags: newTags });
      onUpdated(data);
    } catch (e) { toast.error(extractErr(e)); }
  };
  const add = () => {
    const t = (val || "").trim();
    if (!t) { setAdding(false); return; }
    if (!tags.includes(t)) patch([...tags, t]);
    setVal(""); setAdding(false);
  };
  const remove = (t) => patch(tags.filter(x => x !== t));
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {tags.map(t => (
        <span key={t} data-testid={`sidebar-tag-${t}`} className="group inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-sm">
          {t}
          <button
            data-testid={`sidebar-tag-remove-${t}`}
            onClick={() => remove(t)}
            className="opacity-40 group-hover:opacity-100 hover:text-red-600"
          ><X size={9} /></button>
        </span>
      ))}
      {adding ? (
        <input
          data-testid="sidebar-tag-input"
          autoFocus
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onBlur={add}
          onKeyDown={(e) => {
            if (e.key === "Enter") add();
            if (e.key === "Escape") { setVal(""); setAdding(false); }
          }}
          placeholder="New tag..."
          className="text-[10px] w-24 px-1.5 py-0.5 border border-emerald-500 rounded-sm focus:outline-none"
        />
      ) : (
        <button
          data-testid="sidebar-add-tag-btn"
          onClick={() => setAdding(true)}
          className="text-[10px] px-2 py-0.5 border border-dashed border-slate-300 hover:border-emerald-500 hover:text-emerald-700 text-slate-500 rounded-sm"
        >
          <Plus size={9} className="inline" /> Add Tag
        </button>
      )}
      {tags.length === 0 && !adding && (
        <span className="text-[10px] text-slate-400 italic">No tags yet.</span>
      )}
    </div>
  );
}

function InboxChip({ label, testid, active, onClick, count, accent }) {
  const activeClass = accent === "red"
    ? "bg-red-500 text-white border-red-500"
    : "bg-slate-950 text-white border-slate-950";
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`flex-shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full border transition-colors ${
        active ? activeClass : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
      }`}
    >
      {label} <span className={active ? "opacity-70" : "text-slate-400"}>{count}</span>
    </button>
  );
}

function NotesEditor({ lead, onSaved }) {
  const [val, setVal] = useState(lead.notes || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setVal(lead.notes || ""); }, [lead.id]);
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.patch(`/leads/${lead.id}`, { notes: val }); onSaved(data); toast.success("Notes saved"); }
    catch (e) { toast.error(extractErr(e)); } finally { setSaving(false); }
  };
  return (
    <>
      <textarea
        data-testid="lead-notes"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        rows={4}
        placeholder="Add internal notes..."
        className="w-full px-2 py-1.5 text-sm bg-slate-50 border border-slate-200 rounded-sm focus:outline-none focus:border-emerald-700 resize-none"
      />
      <button data-testid="save-notes" onClick={save} disabled={saving} className="mt-1.5 text-xs font-semibold text-emerald-700 hover:underline">
        {saving ? "Saving…" : "Save notes"}
      </button>
    </>
  );
}

function InboxTemplateModal({ conversationId, leadName, templates, onClose, onSent }) {
  const [selected, setSelected] = useState(null);
  const [params, setParams] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (selected && !params.name) setParams(p => ({ ...p, name: leadName || "老师/家长" }));
    // eslint-disable-next-line
  }, [selected]);

  const paramNames = selected?.param_names || [];
  const previewText = useMemo(() => {
    if (!selected?.body) return "";
    let t = selected.body;
    for (const key of paramNames) {
      t = t.replaceAll(`{{${key}}}`, params[key] || `[${key}]`);
    }
    return t;
  }, [selected, params, paramNames]);

  const submit = async () => {
    if (!selected) return;
    for (const key of paramNames) {
      if (!params[key]?.trim()) { toast.error(`请填写 {{${key}}}`); return; }
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/conversations/${conversationId}/send-template`, {
        wati_template_name: selected.name,
        parameters: paramNames.map(k => ({ name: k, value: params[k] || "" })),
      });
      onSent(data);
    } catch (e) { toast.error(extractErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white border border-slate-200 rounded-sm w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-purple-700 font-semibold">发送 WhatsApp 模板 · Template</div>
            <div className="font-display text-lg font-bold">发送 Template · {leadName}</div>
          </div>
          <button onClick={onClose} data-testid="inbox-tpl-close"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <select
            data-testid="inbox-tpl-select"
            value={selected?.name || ""}
            onChange={(e) => { setSelected(templates.find(t => t.name === e.target.value) || null); setParams({}); }}
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:border-purple-700 bg-white"
          >
            <option value="">-- 选择已批准模板 --</option>
            {templates.map(t => <option key={t.name} value={t.name}>{t.name} · {t.category} · 变量: {(t.param_names||[]).join(",")||"无"}</option>)}
          </select>
          {templates.length === 0 && (
            <div data-testid="inbox-tpl-empty" className="text-[11px] text-amber-700 bg-amber-50 border-l-2 border-amber-400 px-2 py-1 rounded-sm">
              YCloud 暂无已批准的模板，请先在 YCloud 后台提交模板审核。
            </div>
          )}
          {selected && selected.body && (
            <div className="p-2 bg-purple-50 border-l-2 border-purple-400 rounded-sm text-[11px] whitespace-pre-wrap">
              {selected.body.split(/(\{\{[^}]+\}\})/g).map((chunk, i) =>
                chunk.match(/^\{\{[^}]+\}\}$/)
                  ? <span key={i} className="bg-amber-200 px-1 rounded text-amber-900 font-mono font-bold">{chunk}</span>
                  : <span key={i}>{chunk}</span>
              )}
            </div>
          )}
          {paramNames.map(k => (
            <div key={k}>
              <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1 block">Fill {`{{${k}}}`}</label>
              <input
                data-testid={`inbox-tpl-var-${k}`}
                value={params[k] || ""}
                onChange={(e) => setParams({ ...params, [k]: e.target.value })}
                placeholder={k === "name" ? "默认使用联系人姓名" : `变量 ${k} 值`}
                className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-sm focus:outline-none focus:border-purple-700"
              />
            </div>
          ))}
          {selected && previewText && (
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-emerald-700 font-semibold mb-1 block">Preview</label>
              <div data-testid="inbox-tpl-preview" className="p-2 bg-emerald-50 border-l-2 border-emerald-500 rounded-sm text-[11px] whitespace-pre-wrap">{previewText}</div>
            </div>
          )}
          <button data-testid="inbox-tpl-send" onClick={submit} disabled={busy || !selected} className="w-full py-2.5 bg-purple-700 text-white text-sm font-semibold rounded-sm hover:bg-purple-800 disabled:opacity-40 flex items-center justify-center gap-1.5">
            <Send size={13} /> {busy ? "Sending…" : "Send Template · 发送"}
          </button>
        </div>
      </div>
    </div>
  );
}

function NewConversationModal({ onClose, onDone }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("+60");
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [paramValues, setParamValues] = useState({}); // {"1": "value", "2": "value"}

  useEffect(() => {
    setLoading(true);
    api.get("/wati/templates")
      .then(r => {
        const approved = (r.data.templates || []).filter(t => (t.status || "").toUpperCase() === "APPROVED" || t.source === "local");
        setTemplates(approved);
      })
      .catch(e => toast.error(extractErr(e)))
      .finally(() => setLoading(false));
  }, []);

  const varCount = selected
    ? Math.max(selected.variables || 0, (selected.body || "").match(/\{\{\d+\}\}/g)?.length || 0)
    : 0;

  // Default {{1}} to lead name
  useEffect(() => {
    if (selected && varCount >= 1 && !paramValues["1"]) {
      setParamValues(pv => ({ ...pv, "1": name || "" }));
    }
    // eslint-disable-next-line
  }, [selected, name]);

  const previewText = useMemo(() => {
    if (!selected?.body) return "";
    let t = selected.body;
    for (let i = 1; i <= varCount; i++) {
      const v = paramValues[String(i)] || `[{{${i}}}]`;
      t = t.replace(new RegExp(`\\{\\{${i}\\}\\}`, "g"), v);
    }
    return t;
  }, [selected, paramValues, varCount]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("请输入联系人姓名"); return; }
    if (!/^\+\d{7,15}$/.test(phone.trim())) { toast.error("手机号格式：+601... (国际格式)"); return; }
    if (!selected) { toast.error("请选择 WhatsApp 模板"); return; }
    for (let i = 1; i <= varCount; i++) {
      if (!paramValues[String(i)]?.trim()) { toast.error(`请填写变量 {{${i}}}`); return; }
    }
    setBusy(true);
    try {
      const parameters = Array.from({ length: varCount }, (_, i) => ({
        name: String(i + 1),
        value: paramValues[String(i + 1)] || "",
      }));
      const { data } = await api.post("/conversations/start", {
        name: name.trim(),
        phone: phone.trim(),
        wati_template_name: selected.name,
        parameters,
      });
      if (data.status === "failed" || data.status === "queued") {
        toast.warning(`对话已创建 · 但模板发送状态: ${data.status}${data.message?.error ? ` — ${data.message.error}` : ""}`);
      } else {
        toast.success(`对话已创建 · 模板已发送`);
      }
      onDone(data.conversation_id);
    } catch (e) { toast.error(extractErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white border border-slate-200 rounded-sm w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-purple-700 font-semibold">Start New Conversation</div>
            <div className="font-display text-lg font-bold">发起新对话 · WhatsApp 模板</div>
          </div>
          <button onClick={onClose} data-testid="new-conv-close"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1 block">Contact name · 姓名</label>
            <input
              data-testid="new-conv-name"
              required
              value={name}
              onChange={(e)=>setName(e.target.value)}
              placeholder="e.g. 陈老师"
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:border-purple-700"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1 block">WhatsApp phone · 手机号（国际格式）</label>
            <input
              data-testid="new-conv-phone"
              required
              value={phone}
              onChange={(e)=>setPhone(e.target.value)}
              placeholder="+60123456789"
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:border-purple-700 font-mono"
            />
            <div className="text-[10px] text-slate-500 mt-0.5">必须带 + 号 · 号码必须在 WhatsApp 上</div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-purple-700 font-semibold mb-1 block">WhatsApp 模板 · YCloud 已批准</label>
            <select
              data-testid="new-conv-template"
              required
              value={selected?.name || ""}
              onChange={(e) => { setSelected(templates.find(t => t.name === e.target.value) || null); setParamValues({}); }}
              disabled={loading}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:border-purple-700 bg-white disabled:opacity-50"
            >
              <option value="">{loading ? "载入中…" : "-- 选择已批准模板 --"}</option>
              {templates.map(t => (
                <option key={t.name} value={t.name}>
                  {t.name} · {t.category} · {t.status}{t.source === "local" ? " (本地)" : ""}
                </option>
              ))}
            </select>
          </div>
          {selected && (
            <div className="p-2 bg-purple-50 border-l-2 border-purple-400 rounded-sm text-[11px] text-slate-700 whitespace-pre-wrap">
              {selected.body || "(模板内容不可预览)"}
            </div>
          )}
          {selected && varCount > 0 && (
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1 block">Fill variables · 填变量</label>
              <div className="space-y-1.5">
                {Array.from({ length: varCount }, (_, i) => i + 1).map(n => (
                  <div key={n} className="flex items-center gap-2">
                    <span className="font-mono text-xs bg-slate-100 px-2 py-1 rounded-sm min-w-[45px] text-center">{`{{${n}}}`}</span>
                    <input
                      data-testid={`new-conv-var-${n}`}
                      value={paramValues[String(n)] || ""}
                      onChange={(e) => setParamValues({ ...paramValues, [String(n)]: e.target.value })}
                      placeholder={n === 1 ? "默认使用联系人姓名" : `变量 ${n} 值`}
                      className="flex-1 px-2 py-1 text-xs border border-slate-200 rounded-sm focus:outline-none focus:border-purple-700"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
          {selected && (
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-emerald-700 font-semibold mb-1 block">Preview · 预览</label>
              <div data-testid="new-conv-preview" className="p-2 bg-emerald-50 border-l-2 border-emerald-500 rounded-sm text-[11px] whitespace-pre-wrap">
                {previewText || "(填变量后显示)"}
              </div>
            </div>
          )}
          <button
            data-testid="new-conv-submit"
            type="submit"
            disabled={busy}
            className="w-full py-2.5 bg-purple-700 text-white text-sm font-semibold rounded-sm hover:bg-purple-800 disabled:opacity-40 flex items-center justify-center gap-1.5"
          >
            <Send size={13} /> {busy ? "Sending…" : "Send template & start · 发送并创建对话"}
          </button>
        </form>
      </div>
    </div>
  );
}
